/**
 * Admin tab — list of remote-signature requests. Shows their status
 * (pending / signed / expired), lets admin resend the email and copy the
 * link / WhatsApp message for any pending one.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ShieldCheck, RefreshCw, Send, Copy, FileText, CheckCircle2, Clock, AlertTriangle,
  Trash2, Euro,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending: { label: 'Pendiente', icon: Clock, color: 'bg-amber-100 text-amber-800' },
  signed: { label: 'Firmado', icon: CheckCircle2, color: 'bg-green-100 text-green-800' },
  expired: { label: 'Caducado', icon: AlertTriangle, color: 'bg-red-100 text-red-700' },
};

export const SignaturesTab = ({ authHeader }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/signature-requests`, {
        headers: { Authorization: authHeader },
      });
      setItems(res.data.requests || []);
    } catch {
      toast.error('Error al cargar las firmas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authHeader) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authHeader]);

  const filtered = items.filter((b) => {
    const needle = q.trim().toLowerCase();
    if (!needle) return true;
    return [b.customer_name, b.customer_email, b.customer_number, b.date]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(needle));
  });

  const resend = async (b) => {
    try {
      const res = await axios.post(
        `${API}/admin/signature-requests/${b.id}/resend`,
        {},
        { headers: { Authorization: authHeader } },
      );
      if (res.data.email_sent) toast.success('Email reenviado');
      else toast.info('Email no enviado (sin email), copia el enlace abajo');
      // Optimistically refresh the row's reference URL by updating it in place
      setItems((prev) =>
        prev.map((x) =>
          x.id === b.id ? { ...x, _last_url: res.data.signature_url, _last_wa: res.data.whatsapp_message } : x
        )
      );
    } catch {
      toast.error('Error al reenviar');
    }
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copiado');
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  const downloadConsents = async (b) => {
    try {
      const res = await axios.get(`${API}/admin/bookings/${b.id}/consents-pdf`, {
        headers: { Authorization: authHeader },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `consentimientos_${b.customer_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Sin firma aún disponible');
    }
  };

  /**
   * Mark the booking as paid + generate invoice. Used right after the client
   * has signed: admin charges them and the invoice is emailed automatically.
   */
  const chargeAndInvoice = async (b) => {
    if (!window.confirm(
      `¿Cobrar la sesión de ${b.customer_name} (${b.total_price || 0}€) y generar factura?`,
    )) return;
    try {
      // Status='completed' triggers auto-invoice generation + email to client
      await axios.patch(
        `${API}/admin/bookings/${b.id}?status=completed`,
        {},
        { headers: { Authorization: authHeader } },
      );
      toast.success('Sesión completada + factura emitida y enviada por email');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al facturar');
    }
  };

  const deleteRequest = async (b) => {
    if (!window.confirm(
      `¿Eliminar la solicitud de firma de ${b.customer_name}? También se elimina la reserva.`,
    )) return;
    try {
      await axios.delete(`${API}/admin/bookings/${b.id}`, {
        headers: { Authorization: authHeader },
      });
      toast.success('Solicitud eliminada');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  return (
    <div className="space-y-6" data-testid="signatures-tab">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-heading text-halo-charcoal flex items-center gap-2">
            <ShieldCheck size={22} /> Firmas pendientes
          </h2>
          <p className="text-sm text-halo-text">
            {items.length} solicitudes · {' '}
            {items.filter((i) => i.signature_status === 'signed').length} firmadas ·{' '}
            {items.filter((i) => i.signature_status === 'pending').length} pendientes
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </Button>
      </div>

      <Input
        placeholder="Buscar cliente, email, fecha..."
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="max-w-md"
        data-testid="signatures-search"
      />

      <div className="space-y-2">
        {filtered.length === 0 && !loading && (
          <div className="bg-white border border-halo-taupe/15 rounded-xl p-8 text-center">
            <p className="text-sm text-halo-text">
              {q
                ? 'Sin resultados.'
                : 'Aún no hay solicitudes de firma. Crea una desde "Alta presencial" → "Enviar enlace".'}
            </p>
          </div>
        )}
        {filtered.map((b) => {
          const meta = STATUS_META[b.signature_status] || STATUS_META.pending;
          const Icon = meta.icon;
          return (
            <div
              key={b.id}
              className="bg-white border border-halo-taupe/15 rounded-xl p-4"
              data-testid={`signature-row-${b.id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${meta.color}`}>
                      <Icon size={12} /> {meta.label}
                    </span>
                    <span className="font-mono text-xs text-halo-text">{b.customer_number}</span>
                  </div>
                  <p className="font-medium text-halo-charcoal">{b.customer_name}</p>
                  <p className="text-xs text-halo-text">
                    {b.date} · {b.time} · {b.customer_email || 'sin email'}
                  </p>
                  {b.signature_at && (
                    <p className="text-xs text-green-700 mt-1">
                      Firmado el {new Date(b.signature_at).toLocaleString('es-ES')}
                      {b.signer_ip ? ` · IP ${b.signer_ip}` : ''}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {b.signature_status === 'pending' && (
                    <Button size="sm" variant="outline" onClick={() => resend(b)} data-testid={`resend-${b.id}`}>
                      <Send size={14} className="mr-1" /> Reenviar
                    </Button>
                  )}
                  {b._last_url && (
                    <Button size="sm" variant="outline" onClick={() => copyText(b._last_url)}>
                      <Copy size={14} className="mr-1" /> Copiar enlace
                    </Button>
                  )}
                  {b._last_wa && (
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(b._last_wa)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white">
                        WhatsApp
                      </Button>
                    </a>
                  )}
                  {b.signature_status === 'signed' && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => downloadConsents(b)}>
                        <FileText size={14} className="mr-1" /> PDF firma
                      </Button>
                      {b.status !== 'completed' && (
                        <Button
                          size="sm"
                          className="bg-halo-brown hover:bg-halo-charcoal text-halo-cream"
                          onClick={() => chargeAndInvoice(b)}
                          data-testid={`charge-invoice-${b.id}`}
                        >
                          <Euro size={14} className="mr-1" /> Cobrar y facturar
                        </Button>
                      )}
                      {b.status === 'completed' && (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-100 text-green-800">
                          <CheckCircle2 size={12} /> Cobrada · {b.invoice_number || 'facturada'}
                        </span>
                      )}
                    </>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => deleteRequest(b)}
                    className="text-red-500 border-red-200 hover:bg-red-50"
                    data-testid={`delete-signature-${b.id}`}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SignaturesTab;
